// Chemin: C:\gestion-copro\src\pages\admin\DocumentDetailPage.tsx
// Session 58 : Correction bug coproprietaireId → coproprieteIds[0]
// Session 46 : Page détail document (emails archivés + fichiers)
// Session 63 : Migration nouvelle taxonomie GED
// Session 123 : Ajout modification inline (titre, type, catégorie, description, date) + fix visibilité 'proprietaire'

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, updateDoc, serverTimestamp, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { 
  ArrowLeft, 
  Download, 
  Mail, 
  FileText, 
  Calendar, 
  User, 
  Building2,
  Paperclip,
  FolderOpen,
  Trash2,
  Tag,
  Pencil,
  X,
  Save,
  Loader2
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Document, TypeDocumentGED, CategorieDocument } from '../../types/document.types';
import { 
  getDocument, 
  deleteDocument, 
  formatFileSize,
  getTypeDocumentLabel,
  getTypeColor,
  TYPES_DOCUMENT
} from '../../services/document.service';
import { CATEGORIES_DOCUMENT, CATEGORIES_DOCUMENT_OPTIONS } from '../../constants/documentTaxonomy';

// Convertir un Firestore Timestamp en string YYYY-MM-DD pour input date
const timestampToInputDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toISOString().split('T')[0];
};

export default function DocumentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { userProfile } = useAuth();

  const [document, setDocument] = useState<Document | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Session 123 : États modification
  const [isEditing, setIsEditing] = useState(false);
  const [editTitre, setEditTitre] = useState('');
  const [editType, setEditType] = useState<TypeDocumentGED>('autre');
  const [editCategorie, setEditCategorie] = useState<CategorieDocument>('administratif');
  const [editDescription, setEditDescription] = useState('');
  const [editDateDocument, setEditDateDocument] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const coproprieteId = userProfile?.coproprieteIds?.[0];

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (!id) return;

    const loadDocument = async () => {
      try {
        setLoading(true);
        const doc = await getDocument(id);
        if (!doc) {
          setError('Document non trouvé');
          return;
        }
        setDocument(doc);
      } catch (err) {
        console.error('Erreur chargement document:', err);
        setError('Erreur lors du chargement du document');
      } finally {
        setLoading(false);
      }
    };

    loadDocument();
  }, [id]);

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'Non renseigné';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async () => {
    if (!document || !coproprieteId) return;
    
    setDeleting(true);
    try {
      await deleteDocument(document.id, coproprieteId);
      navigate('/admin/documents');
    } catch (err) {
      console.error('Erreur suppression:', err);
      setError('Erreur lors de la suppression');
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // Session 123 : Initialiser les champs d'édition
  const startEditing = () => {
    if (!document) return;
    setEditTitre(document.titre || '');
    setEditType((document.type as TypeDocumentGED) || 'autre');
    setEditCategorie((document.categorie as CategorieDocument) || 'administratif');
    setEditDescription(document.description || '');
    setEditDateDocument(timestampToInputDate(document.dateDocument));
    setSaveError(null);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setSaveError(null);
  };

  // Session 123 : Sauvegarder les modifications
  const handleSave = async () => {
    if (!id || !editTitre.trim()) {
      setSaveError('Le titre est obligatoire.');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      const docRef = doc(db, 'documents', id);
      const updates: Record<string, any> = {
        titre: editTitre.trim(),
        type: editType,
        categorie: editCategorie,
        description: editDescription.trim() || null,
        updatedAt: serverTimestamp()
      };

      if (editDateDocument) {
        updates.dateDocument = Timestamp.fromDate(new Date(editDateDocument));
      }

      await updateDoc(docRef, updates);

      // Mettre à jour l'état local
      setDocument((prev: any) => {
        if (!prev) return prev;
        return {
          ...prev,
          titre: editTitre.trim(),
          type: editType,
          categorie: editCategorie,
          description: editDescription.trim() || null,
          dateDocument: editDateDocument ? { toDate: () => new Date(editDateDocument) } : prev.dateDocument
        };
      });

      setIsEditing(false);
      setSuccessMessage('Document mis à jour');
    } catch (err) {
      console.error('Erreur sauvegarde:', err);
      setSaveError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Détecter si c'est un email archivé
  const isEmail = document?.sourceEmail !== undefined;

  // Session 63 : Obtenir le label de la catégorie
  const getCategorieLabel = (categorie?: string) => {
    if (!categorie) return null;
    return CATEGORIES_DOCUMENT[categorie as keyof typeof CATEGORIES_DOCUMENT] || categorie;
  };

  // Session 123 : Label de visibilité (gère proprietaire)
  const getVisibiliteLabel = (visibilite?: string) => {
    if (visibilite === 'public') return <span className="text-green-600">Visible par tous</span>;
    if (visibilite === 'proprietaire') return <span className="text-violet-600">Document personnel</span>;
    return <span className="text-gray-600">Conseil Syndical uniquement</span>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  if (error || !document) {
    return (
      <AdminLayout>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">{error || 'Document non trouvé'}</p>
          <Link to="/admin/documents" className="text-blue-600 hover:underline mt-2 inline-block">
            Retour aux documents
          </Link>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">

        {/* Message succès */}
        {successMessage && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMessage}
          </div>
        )}

        {/* En-tête */}
        {isEditing ? (
          /* ============================================ */
          /* MODE ÉDITION — Session 123                  */
          /* ============================================ */
          <div className="bg-white rounded-lg shadow p-6 space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Pencil className="w-5 h-5 text-blue-600" />
                Modifier le document
              </h2>
              <button
                onClick={cancelEditing}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
                title="Annuler"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Titre */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
              <input
                type="text"
                value={editTitre}
                onChange={(e) => setEditTitre(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Titre du document"
              />
            </div>

            {/* Type + Catégorie */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                <select
                  value={editType}
                  onChange={(e) => setEditType(e.target.value as TypeDocumentGED)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {TYPES_DOCUMENT.map(t => (
                    <option key={t.value} value={t.value}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie</label>
                <select
                  value={editCategorie}
                  onChange={(e) => setEditCategorie(e.target.value as CategorieDocument)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {CATEGORIES_DOCUMENT_OPTIONS.map(c => (
                    <option key={c.value} value={c.value}>{c.code} - {c.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Date du document */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date du document</label>
              <input
                type="date"
                value={editDateDocument}
                onChange={(e) => setEditDateDocument(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
              <textarea
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Description du document..."
              />
            </div>

            {/* Erreur sauvegarde */}
            {saveError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                {saveError}
              </div>
            )}

            {/* Boutons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={cancelEditing}
                disabled={saving}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !editTitre.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Enregistrer
              </button>
            </div>
          </div>
        ) : (
          /* ============================================ */
          /* MODE LECTURE                                 */
          /* ============================================ */
          <div className="flex justify-between items-start">
            <div>
              <button
                onClick={() => navigate(-1)}
                className="text-gray-600 hover:text-gray-900 mb-2 inline-flex items-center text-sm gap-1"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
              <div className="flex items-center gap-3">
                {isEmail ? (
                  <Mail className="w-6 h-6 text-blue-600" />
                ) : (
                  <FileText className="w-6 h-6 text-gray-600" />
                )}
                <h1 className="text-2xl font-bold text-gray-900">{document.titre}</h1>
              </div>
              {/* Session 63 : Utilise getTypeDocumentLabel et getTypeColor */}
              <div className="flex items-center gap-2 mt-1">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${getTypeColor(document.type)}`}>
                  {getTypeDocumentLabel(document.type)}
                </span>
                {/* Session 63 : Affiche la catégorie si présente */}
                {document.categorie && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                    {getCategorieLabel(document.categorie)}
                  </span>
                )}
                {isEmail && (
                  <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                    Email archivé
                  </span>
                )}
              </div>
            </div>
            
            <div className="flex gap-2">
              {/* Session 123 : Bouton Modifier */}
              <button
                onClick={startEditing}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Pencil className="w-4 h-4" />
                Modifier
              </button>
              {document.fichierUrl && (
                <a
                  href={document.fichierUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  <Download className="w-4 h-4" />
                  Télécharger
                </a>
              )}
              <button
                onClick={() => setShowDeleteModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
              >
                <Trash2 className="w-4 h-4" />
                Supprimer
              </button>
            </div>
          </div>
        )}

        {/* Contenu — masqué en mode édition */}
        {!isEditing && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Colonne principale */}
            <div className="lg:col-span-2 space-y-6">
              
              {/* Contenu email archivé */}
              {isEmail && document.sourceEmail && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Mail className="w-5 h-5 text-blue-600" />
                    Contenu de l'email
                  </h2>
                  
                  {/* En-têtes email */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4 text-sm space-y-2">
                    <div>
                      <span className="font-medium text-gray-700">De :</span>
                      <span className="ml-2 text-gray-900">{document.sourceEmail.from}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">À :</span>
                      <span className="ml-2 text-gray-900">{document.sourceEmail.to}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Objet :</span>
                      <span className="ml-2 text-gray-900">{document.sourceEmail.subject}</span>
                    </div>
                    <div>
                      <span className="font-medium text-gray-700">Date :</span>
                      <span className="ml-2 text-gray-900">{formatDate(document.sourceEmail.date)}</span>
                    </div>
                  </div>

                  {/* Corps de l'email */}
                  {document.contenuTexte && (
                    <div className="prose prose-sm max-w-none">
                      <pre className="whitespace-pre-wrap font-sans text-gray-700 text-sm">
                        {document.contenuTexte}
                      </pre>
                    </div>
                  )}

                  {/* Pièces jointes */}
                  {document.pieceJointes && document.pieceJointes.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <h3 className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                        <Paperclip className="w-4 h-4" />
                        Pièces jointes ({document.pieceJointes.length})
                      </h3>
                      <div className="space-y-2">
                        {document.pieceJointes.map((url: string, index: number) => (
                          <a
                            key={index}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block text-sm text-blue-600 hover:underline"
                          >
                            Pièce jointe {index + 1}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Description (si pas email) */}
              {!isEmail && document.description && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold mb-4">Description</h2>
                  <p className="text-gray-700 whitespace-pre-wrap">{document.description}</p>
                </div>
              )}

              {/* Session 63 : Résumé SENTINELLE si présent */}
              {document.resume && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-indigo-600" />
                    Résumé (SENTINELLE)
                  </h2>
                  <p className="text-gray-700 whitespace-pre-wrap">{document.resume}</p>
                </div>
              )}
            </div>

            {/* Colonne latérale - Métadonnées */}
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-lg font-semibold mb-4">Informations</h2>
                
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm text-gray-500 flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      Date du document
                    </dt>
                    <dd className="text-gray-900 mt-1">{formatDate(document.dateDocument)}</dd>
                  </div>

                  {document.interlocuteurNom && (
                    <div>
                      <dt className="text-sm text-gray-500 flex items-center gap-1">
                        <Building2 className="w-4 h-4" />
                        Interlocuteur
                      </dt>
                      <dd className="text-gray-900 mt-1">{document.interlocuteurNom}</dd>
                    </div>
                  )}

                  {document.fichierNom && (
                    <div>
                      <dt className="text-sm text-gray-500 flex items-center gap-1">
                        <FileText className="w-4 h-4" />
                        Fichier
                      </dt>
                      <dd className="text-gray-900 mt-1 text-sm break-all">{document.fichierNom}</dd>
                    </div>
                  )}

                  {/* Session 63 : Nom fichier normalisé si présent */}
                  {document.nomFichierNormalise && (
                    <div>
                      <dt className="text-sm text-gray-500">Nom normalisé</dt>
                      <dd className="text-gray-900 mt-1 text-sm break-all font-mono bg-gray-50 px-2 py-1 rounded">
                        {document.nomFichierNormalise}
                      </dd>
                    </div>
                  )}

                  {document.fichierTaille > 0 && (
                    <div>
                      <dt className="text-sm text-gray-500">Taille</dt>
                      <dd className="text-gray-900 mt-1">{formatFileSize(document.fichierTaille)}</dd>
                    </div>
                  )}

                  <div>
                    <dt className="text-sm text-gray-500 flex items-center gap-1">
                      <User className="w-4 h-4" />
                      Archivé par
                    </dt>
                    <dd className="text-gray-900 mt-1">{document.uploadeParNom || 'Non renseigné'}</dd>
                  </div>

                  <div>
                    <dt className="text-sm text-gray-500">Archivé le</dt>
                    <dd className="text-gray-900 mt-1">{formatDate(document.createdAt)}</dd>
                  </div>

                  {/* Session 123 : Visibilité — gère 'proprietaire' */}
                  {document.visibilite && (
                    <div>
                      <dt className="text-sm text-gray-500">Visibilité</dt>
                      <dd className="text-gray-900 mt-1">
                        {getVisibiliteLabel(document.visibilite)}
                      </dd>
                    </div>
                  )}
                </dl>
              </div>

              {/* Affaire liée */}
              {document.affaireId && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <FolderOpen className="w-5 h-5 text-orange-600" />
                    Affaire liée
                  </h2>
                  {document.affaireNom && (
                    <p className="text-gray-700 mb-2">{document.affaireNom}</p>
                  )}
                  <Link
                    to={`/admin/affaires/${document.affaireId}`}
                    className="text-blue-600 hover:underline"
                  >
                    Voir l'affaire
                  </Link>
                </div>
              )}

              {/* Session 63 : Statut SENTINELLE si présent */}
              {document.sentinelle && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <Tag className="w-5 h-5 text-indigo-600" />
                    Analyse SENTINELLE
                  </h2>
                  <dl className="space-y-2 text-sm">
                    <div>
                      <dt className="text-gray-500">Statut</dt>
                      <dd className="mt-1">
                        {document.sentinelle.statut === 'accepte' && (
                          <span className="text-green-600 font-medium">✓ Accepté</span>
                        )}
                        {document.sentinelle.statut === 'rejete' && (
                          <span className="text-red-600 font-medium">✗ Rejeté</span>
                        )}
                        {document.sentinelle.statut === 'corrige' && (
                          <span className="text-orange-600 font-medium">✎ Corrigé</span>
                        )}
                        {document.sentinelle.statut === 'en_attente' && (
                          <span className="text-gray-600 font-medium">⏳ En attente</span>
                        )}
                      </dd>
                    </div>
                    {document.sentinelle.valideParNom && (
                      <div>
                        <dt className="text-gray-500">Validé par</dt>
                        <dd className="text-gray-900 mt-1">{document.sentinelle.valideParNom}</dd>
                      </div>
                    )}
                    {document.sentinelle.valideAt && (
                      <div>
                        <dt className="text-gray-500">Validé le</dt>
                        <dd className="text-gray-900 mt-1">{formatDate(document.sentinelle.valideAt)}</dd>
                      </div>
                    )}
                  </dl>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Modal Suppression */}
        {showDeleteModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                  <Trash2 className="w-6 h-6 text-red-600" />
                </div>

                <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
                  Supprimer ce document ?
                </h3>

                <p className="text-sm font-medium text-gray-900 text-center mb-4 px-4 py-2 bg-gray-100 rounded">
                  {document.titre}
                </p>

                <p className="text-sm text-red-600 text-center mb-6">
                  Cette action est irréversible.
                </p>

                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(false)}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleDelete}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {deleting ? 'Suppression...' : 'Supprimer'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
